'use client';

/** @jsxImportSource @emotion/react */
import {
  Badge,
  Button,
  Card,
  Container,
  FlexItem,
  FlexLayout,
  Icon,
  Image,
  responsive,
  Typography,
} from '@wci-tech/core-ui';
import { css } from '@emotion/react';
import { useTranslations } from 'next-intl';
import { BsArrowUpRight } from 'react-icons/bs';

import { useThemeMode } from '@/context';

import { useStyles } from '../web-platform/WebPlatform.styles';

export const createBackendImage = (index: number, isDark: boolean) => {
  const themeSuffix = isDark ? '' : 'Light';

  return {
    src: `/images/backendInfra${index}Mobile${themeSuffix}.png`,
    sources: [
      {
        srcSet: `/images/backendInfra${index}Desktop${themeSuffix}.png`,
        media: '(min-width: 1024px)',
        type: 'image/png',
      },
      {
        srcSet: `/images/backendInfra${index}Tablet${themeSuffix}.png`,
        media: '(min-width: 768px)',
        type: 'image/png',
      },
    ],
  };
};

const BackendPlatform = () => {
  const { isDark } = useThemeMode();
  const styles = useStyles();
  const t = useTranslations('BackendPlatform');

  const backendInfraCards = [
    {
      id: 'identity&accessmanagement',
      imageIndex: 1,
      title: t('cards.identity&accessmanagement.title'),
      description: t('cards.identity&accessmanagement.description'),
      highlight: t('cards.identity&accessmanagement.highlight'),
      buttonText: t('cards.identity&accessmanagement.buttonText'),
      showButton: false,
      chip: {
        label: t('cards.identity&accessmanagement.badge'),
        bg: '#002F2E',
        color: '#4EE1B5',
      },
    },
    {
      id: 'apigateway',
      imageIndex: 2,
      title: t('cards.apigateway.title'),
      description: t('cards.apigateway.description'),
      highlight: t('cards.apigateway.highlight'),
      buttonText: t('cards.apigateway.buttonText'),
      showButton: false,
      chip: {
        label: t('cards.apigateway.badge'),
        bg: '#3E2F00',
        color: '#FFC400',
      },
    },
  ];

  return (
    <section css={styles.wrapper} id="backend-platform">
      <Container css={styles.container}>
        <Typography variant="h2" customStyles={styles.sectionTitle}>
          {t('sectionTitle')}
        </Typography>

        {backendInfraCards.map((card, index) => {
          const isContentLeft = index % 2 === 0;
          const image = createBackendImage(card.imageIndex, isDark);

          return (
            <Card
              key={card.id}
              id={card.id}
              appearance="basic"
              customStyles={{
                container: styles.cardContainer,
              }}
            >
              {/* Mobile only: Badge -> Title */}
              <div css={styles.mobileHeader}>
                {card.chip.label && (
                  <div css={styles.badgeWrapper}>
                    <Badge
                      content={card.chip.label}
                      shape="pill"
                      effect="lightSweep"
                      customStyles={{
                        badge: styles.badge(card.chip.bg, card.chip.color),
                      }}
                    />
                  </div>
                )}

                <Typography variant="h3" customStyles={styles.title}>
                  {card.title}
                </Typography>
              </div>

              <FlexLayout
                direction={{
                  default: 'column',
                  md: 'row',
                }}
                wrap="nowrap"
                columns={12}
                alignItems={{
                  default: 'flex-start',
                  md: 'stretch',
                }}
                justifyContent="flex-start"
                fullWidth
                gap={{ default: 20, md: 0 }}
                padding={{ default: 0 }}
              >
                <FlexItem
                  columnSpan={{ default: 12, md: 6 }}
                  basis={{ default: '100%', md: '50%' }}
                  customStyles={{
                    flexItem: styles.contentItem(isContentLeft),
                  }}
                >
                  <div css={styles.contentCol}>
                    {/* Tablet + Desktop: original Badge -> Title */}
                    <div css={styles.desktopHeader}>
                      {card.chip.label && (
                        <div css={styles.badgeWrapper}>
                          <Badge
                            content={card.chip.label}
                            shape="pill"
                            effect="lightSweep"
                            customStyles={{
                              badge: styles.badge(card.chip.bg, card.chip.color),
                            }}
                          />
                        </div>
                      )}

                      <Typography variant="h3" customStyles={styles.title}>
                        {card.title}
                      </Typography>
                    </div>

                    <Typography variant="p" customStyles={styles.description}>
                      {card.description}
                    </Typography>

                    <Typography variant="h4" customStyles={styles.highlightText}>
                      {card.highlight}
                    </Typography>

                    {card.showButton && (
                      <div css={styles.buttonContainer}>
                        <Button
                          variant={isDark ? 'secondary' : 'primary'}
                          appearance="outline"
                          shape="pill"
                          href="/coming-soon"
                          effect="icon-shift"
                          iconPlacement="right"
                          icon={
                            <Icon
                              name={BsArrowUpRight}
                              size={{
                                default: '14px',
                                lg: '16px',
                              }}
                            />
                          }
                          css={css`
                            ${responsive.create({
                              width: {
                                default: '100%',
                                md: 'auto',
                              },
                            })}
                          `}
                          customStyles={{
                            icon: {
                              right: styles.btnIcon,
                            },
                          }}
                        >
                          {card.buttonText}
                        </Button>
                      </div>
                    )}
                  </div>
                </FlexItem>

                <FlexItem
                  columnSpan={{ default: 12, md: 6 }}
                  basis={{ default: '100%', md: '50%' }}
                  customStyles={{
                    flexItem: styles.imageItem(isContentLeft),
                  }}
                >
                  <div css={styles.imageCol}>
                    <Image
                      key={`${card.id}-${isDark ? 'dark' : 'light'}`}
                      src={image.src}
                      sources={image.sources}
                      isImgDecorative={false}
                      altText={card.title}
                      width="100%"
                      aspectRatio="4 / 3"
                      objectFit="contain"
                      customStyles={styles.image}
                    />
                  </div>
                </FlexItem>
              </FlexLayout>
            </Card>
          );
        })}
      </Container>
    </section>
  );
};

export default BackendPlatform;
